import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  fetchMasterOptions,
  isApiBackedMasterKey,
  MASTERS_UPDATED_EVENT,
  type MasterKey,
  type MasterOption,
} from '../services/mastersService'

type UseMastersState = {
  data: MasterOption[]
  loading: boolean
  error: string | null
}

/**
 * Generic master-list hook.
 * - UI can use this now with local masters.
 * - Later set `useApi: true` per screen/module to switch to backend masters.
 */
export function useMasters(key: MasterKey, options?: { useApi?: boolean }) {
  const [state, setState] = useState<UseMastersState>({
    data: [],
    loading: true,
    error: null,
  })

  const useApi = options?.useApi ?? isApiBackedMasterKey(key)

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }))
    try {
      const data = await fetchMasterOptions(key, { useApi })
      setState({ data, loading: false, error: null })
    } catch (error) {
      setState({
        data: [],
        loading: false,
        error: error instanceof Error ? error.message : 'Unable to load master list.',
      })
    }
  }, [key, useApi])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (useApi || typeof window === 'undefined') return
    const onUpdated = () => {
      void load()
    }
    window.addEventListener(MASTERS_UPDATED_EVENT, onUpdated)
    return () => window.removeEventListener(MASTERS_UPDATED_EVENT, onUpdated)
  }, [load, useApi])

  const byValue = useMemo(() => {
    const map = new Map<string, MasterOption>()
    for (const item of state.data) map.set(item.value, item)
    return map
  }, [state.data])

  return {
    ...state,
    reload: load,
    byValue,
  }
}
